import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <NavWrapper scrolled={scrolled}>
      <NavContainer>
        <LogoContainer to="/">
          <LogoImage
            src="/images/bmg_logo.png"
            alt="Baltimore Grillz & Custom Jewelry Logo"
          />
        </LogoContainer>

        <NavLinks>
          <NavItem>
            <NavLink to="/" active={location.pathname === "/"}>
              Home
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/about" active={location.pathname === "/about"}>
              About
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/services" active={location.pathname === "/services"}>
              Services
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/gallery" active={location.pathname === "/gallery"}>
              Gallery
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/contact" active={location.pathname === "/contact"}>
              Contact
            </NavLink>
          </NavItem>
          <ButtonItem>
            <ContactButton to="/contact">Get in Touch</ContactButton>
          </ButtonItem>
        </NavLinks>

        <MobileMenuToggle onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuToggle>

        <AnimatePresence>
          {isOpen && (
            <MobileMenu
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.4 }}
            >
              <MobileNavItem>
                <MobileNavLink to="/" active={location.pathname === "/"}>
                  Home
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink
                  to="/about"
                  active={location.pathname === "/about"}
                >
                  About
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink
                  to="/services"
                  active={location.pathname === "/services"}
                >
                  Services
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink
                  to="/gallery"
                  active={location.pathname === "/gallery"}
                >
                  Gallery
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink
                  to="/contact"
                  active={location.pathname === "/contact"}
                >
                  Contact
                </MobileNavLink>
              </MobileNavItem>
              <MobileContactButton to="/contact">
                Get in Touch
              </MobileContactButton>
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavContainer>
    </NavWrapper>
  );
};

// Styled Components
const NavWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: var(--z-navbar);
  transition: var(--transition-medium);
  background: ${(props) =>
    props.scrolled ? "rgba(0, 0, 0, 0.95)" : "rgba(0, 0, 0, 0.5)"};
  backdrop-filter: blur(15px);
  box-shadow: ${(props) =>
    props.scrolled ? "0 5px 20px rgba(0, 0, 0, 0.5)" : "none"};
  border-bottom: 1px solid
    ${(props) =>
    props.scrolled ? "rgba(255, 215, 0, 0.2)" : "rgba(255, 215, 0, 0.05)"};
`;

// Keyframes for subtle logo glow
const logoGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.6));
  }
  50% {
    filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.8));
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem var(--spacing-md);
  max-width: 1400px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 992px) {
    padding: 0.8rem var(--spacing-sm);
  }
`;

const LogoContainer = styled(Link)`
  display: block;
  z-index: var(--z-elevated);
  transition: var(--transition-fast);

  &:hover {
    transform: scale(1.05);
  }
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  object-fit: contain;
  transition: all 0.3s ease;
  animation: ${logoGlow} 3s ease-in-out infinite;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 45px;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);

  @media (max-width: 992px) {
    display: none;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const ButtonItem = styled(NavItem)`
  margin-left: var(--spacing-sm);
`;

const NavLink = styled(Link)`
  color: ${(props) => (props.active ? "var(--gold)" : "white")};
  font-size: 0.95rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 0;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${(props) => (props.active ? "100%" : "0")};
    height: 2px;
    background: var(--gold-gradient);
    transition: width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: ${(props) => (props.active ? "0 0 8px rgba(255, 215, 0, 0.5)" : "none")};
  }

  &:hover {
    color: var(--gold);
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);

    &:after {
      width: 100%;
      box-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
    }
  }
`;

const ContactButton = styled(Link)`
  background: var(--gold-gradient);
  color: var(--black);
  padding: 0.6rem 1.4rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: var(--transition-medium);
  display: inline-block;
  position: relative;
  overflow: hidden;
  box-shadow: var(--gold-glow);

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: var(--transition-fast);
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.3);

    &:before {
      left: 100%;
    }
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: transparent;
  color: var(--gold);
  font-size: 1.5rem;
  z-index: var(--z-elevated);

  @media (max-width: 992px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 400px;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  z-index: var(--z-elevated);
  backdrop-filter: blur(10px);
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.5);
  border-left: 1px solid rgba(255, 215, 0, 0.1);
`;

const MobileNavItem = styled.div`
  width: 100%;
  text-align: center;
`;

const MobileNavLink = styled(Link)`
  color: ${(props) => (props.active ? "var(--gold)" : "white")};
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: var(--transition-fast);
  padding: 0.8rem 0;
  display: block;
  width: 100%;

  &:hover {
    color: var(--gold);
    transform: translateX(5px);
  }
`;

const MobileContactButton = styled(Link)`
  background: var(--gold-gradient);
  color: var(--black);
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: var(--transition-medium);
  margin-top: var(--spacing-md);
  display: block;
  text-align: center;
  box-shadow: var(--gold-glow);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.3);
  }
`;

export default Header;
